import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSessionId, getToken, isGuestUser } from '@/utils/storage';
import { getStoredGuestExamUser } from '@/api/exam';
import { SkillsRadarChart } from '@/components/SkillsRadarChart';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Lock, Unlock, TrendingUp, Target, BookOpen, MessageCircle, Crown, Star } from 'lucide-react';
import '../styles/feedback.css';

const improvementTips = [
  'Practice daily conversation with native speakers',
  'Record yourself speaking and analyze pronunciation',
  'Write daily journal entries in French',
  'Read business newspapers and magazines',
  'Listen to podcasts at normal speed',
  'Join speaking clubs or language exchange programs'
];

interface ExamResults {
  id: string;
  global_cefr: string;
  fluency: number | null;
  pronunciation: number | null;
  grammar: number;
  vocabulary: number;
  comprehension: number;
  finalAverage: number;
}

const ExamResults: React.FC = () => {
  const [results, setResults] = useState<ExamResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userType, setUserType] = useState<'guest' | 'paid'>('paid'); // Default to paid since they completed the exam
  const [isGuest, setIsGuest] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is a guest using the same logic as fetchResults
    const paidToken = getToken();
    const guestUser = getStoredGuestExamUser();
    const guestToken = guestUser?.token ?? localStorage.getItem("guestToken");
    const isGuest = !paidToken && !!guestToken;
  
    setIsGuest(isGuest);
    setUserType(isGuest ? 'guest' : 'paid');

    const fetchResults = async () => {
      try {
        const sessionId = getSessionId();
        
        // 1) Get tokens
        const paidToken = getToken();
        const guestUser = getStoredGuestExamUser();
        const guestToken = guestUser?.token ?? localStorage.getItem("guestToken");

        // 2) Decide which mode
        const isGuest = !paidToken && !!guestToken;
        const token = paidToken || guestToken;
        
        if (!sessionId || !token) {
          setError('Session or authentication not found');
          setLoading(false);
          return;
        }

        console.log('🎯 EXAM RESULTS - Fetching exam results...');
        
        // Check if we're in comprehensive feedback mode
        const comprehensiveFeedbackData = localStorage.getItem('combined_feedback_data');
        if (comprehensiveFeedbackData) {
          console.log('🎯 EXAM RESULTS - Comprehensive feedback data detected, skipping function calls');
          console.log('🎯 EXAM RESULTS - Redirecting to comprehensive feedback results...');
          navigate('/comprehensive-feedback-results');
          return;
        }
        
        // Step 1: Call fluency metrics first
        console.log('🎯 EXAM RESULTS - Step 1: Calling fluency metrics analysis...');
        
        try {
          const fluencyPayload = {
            session_id: sessionId,
            task_attempt_id: null // Will be determined by the fluency function
          };
          
          const fluencyResponse = await fetch('https://nluntjftnyzlxhatvvks.supabase.co/functions/v1/feedback-fluency-metrics', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(fluencyPayload)
          });

          if (fluencyResponse.ok) {
            const fluencyResult = await fluencyResponse.json();
            console.log('✅ EXAM RESULTS - Fluency metrics completed:', fluencyResult);
          } else {
            const errorText = await fluencyResponse.text();
            console.error('❌ EXAM RESULTS - Fluency metrics failed:', fluencyResponse.status, errorText);
            // Continue anyway - combined feedback can work without fluency metrics
          }
        } catch (fluencyError) {
          console.error('❌ EXAM RESULTS - Fluency metrics error:', fluencyError);
          // Continue anyway - combined feedback can work without fluency metrics
        }
        
        // Step 2: Wait for fluency metrics to be stored in database
        console.log('⏳ EXAM RESULTS - Waiting for fluency metrics to be stored...');
        await new Promise(resolve => setTimeout(resolve, 5000)); // 5 second wait
        
        // Step 3: Call combined feedback analysis
        console.log('🎯 EXAM RESULTS - Step 2: Calling combined feedback analysis...');
        
        const feedbackPayload: any = { 
          session_id: sessionId 
        };
        
        if (isGuest) {
          feedbackPayload.guest_id = guestUser?.user_id;
          feedbackPayload.is_guest = true;
        }
        
        const feedbackResponse = await fetch('https://nluntjftnyzlxhatvvks.supabase.co/functions/v1/combined-feedback-analysis', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(feedbackPayload)
        });

        if (!feedbackResponse.ok) {
          throw new Error(`Failed to generate feedback: ${feedbackResponse.status}`);
        }

        const feedbackData = await feedbackResponse.json();
        console.log('✅ EXAM RESULTS - Combined feedback completed:', feedbackData);
        
        // Use the data returned from combined feedback analysis
        if (feedbackData.success && feedbackData.feedback) {
          const feedback = feedbackData.feedback;
          console.log('✅ EXAM RESULTS - Using data from combined feedback analysis:', feedback);
          
          // Transform the data to match our interface
          const examResults = {
            id: sessionId,
            global_cefr: feedback.overall?.cefr_level || 'B2',
            fluency: feedback.radar?.scores?.fluency || 0,
            pronunciation: feedback.radar?.scores?.pronunciation || 0,
            grammar: feedback.radar?.scores?.grammar || 0,
            vocabulary: feedback.radar?.scores?.vocabulary || 0,
            comprehension: feedback.radar?.scores?.comprehension || 0,
            finalAverage: Math.round((
              (feedback.radar?.scores?.fluency || 0) +
              (feedback.radar?.scores?.pronunciation || 0) +
              (feedback.radar?.scores?.grammar || 0) +
              (feedback.radar?.scores?.vocabulary || 0) +
              (feedback.radar?.scores?.comprehension || 0)
            ) / 5)
          };
          
          setResults(examResults);
        } else {
          throw new Error('No exam results found in feedback data');
        }
        setLoading(false);
      } catch (err) {
        console.error('❌ EXAM RESULTS - Error fetching results:', err);
        setError(err instanceof Error ? err.message : 'Failed to load results');
        setLoading(false);
      }
    };

    fetchResults();
  }, []);

  const handleUpgrade = () => {
    // Redirect to pricing page for payment
    navigate('/pricing');
  };


  const getCEFRColor = (cefr: string) => {
    switch (cefr) {
      case 'C2': return '#10B981';
      case 'C1': return '#34D399';
      case 'B2': return '#F59E0B';
      case 'B1': return '#F97316';
      case 'A2': return '#EF4444';
      case 'A1': return '#DC2626';
      default: return '#6B7280';
    }
  };

  const formatScore = (score: number) => {
    return Math.round(score);
  };

  if (loading) {
    return (
      <div className="feedback-container">
        <div className="feedback-content">
          <div className="header-section">
            <h1 className="header-title">Processing Your Results</h1>
            <p className="header-subtitle">Please wait while we calculate your final scores...</p>
            <div className="loading-spinner" style={{ 
              width: '50px', 
              height: '50px', 
              border: '5px solid #f3f3f3', 
              borderTop: '5px solid #005fcc', 
              borderRadius: '50%', 
              animation: 'spin 1s linear infinite',
              margin: '20px auto'
            }}></div>
          </div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="feedback-container">
        <div className="feedback-content">
          <div className="header-section">
            <h1 className="header-title" style={{ color: '#6B7280' }}>Feedback Not Found</h1>
            <p className="header-subtitle" style={{ color: '#666' }}>We couldn't retrieve your exam results at this time. Please try again later.</p>
            <Button onClick={() => navigate('/dashboard')} style={{ marginTop: '20px' }}>
              Return to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="feedback-container">
        <div className="feedback-content">
          <div className="header-section">
            <h1 className="header-title" style={{ color: '#6B7280' }}>Feedback Not Found</h1>
            <p className="header-subtitle" style={{ color: '#666' }}>We couldn't retrieve your exam results at this time. Please try again later.</p>
            <Button onClick={() => navigate('/dashboard')} style={{ marginTop: '20px' }}>
              Return to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-container">

      <div className="feedback-content">
        {/* Header */}
        <div className="header-section" style={{ marginTop: '40px' }}>
          <h1 className="header-title">Your Progress Report</h1>
          <p className="header-subtitle">Track your language learning journey</p>
        </div>


        {/* Main Content Grid */}
        <div className="main-grid">
          {/* Combined CEFR Level and Skills Chart */}
          <div className={`chart-card ${userType === 'guest' ? 'blurred-content' : ''}`}>
            <div className="chart-header">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <h2 className="chart-title">
                  <TrendingUp size={24} />
                  Skills Breakdown
                </h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <div className="level-badge" style={{ backgroundColor: getCEFRColor(results.global_cefr) }}>
                    Level: {results.global_cefr}
                  </div>
                  <div style={{ 
                    fontSize: '18px', 
                    fontWeight: 'bold', 
                    color: '#333',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}>
                    <span style={{ color: '#666' }}>{formatScore(results.finalAverage)}/100</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Radar Chart */}
            <div style={{ minHeight: '250px' }}>
              <SkillsRadarChart 
                data={[
                  { skill: 'Grammar', score: results.grammar },
                  { skill: 'Vocabulary', score: results.vocabulary },
                  { skill: 'Comprehension', score: results.comprehension },
                  { skill: 'Fluency', score: results.fluency || 0 },
                  { skill: 'Pronunciation', score: results.pronunciation || 0 }
                ]}
              />
            </div>
          </div>

          {userType === "guest" && (
            <div className="overlay" style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.9)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: '12px'
            }}>
              <div style={{ textAlign: 'center', padding: '20px', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {/* CEFR Level Card */}
                {results && (
                  <div style={{
                    background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '24px',
                    boxShadow: '0 4px 16px rgba(245, 158, 11, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    width: 'fit-content',
                    minWidth: '200px'
                  }}>
                    {/* Background decoration */}
                    <div style={{
                      position: 'absolute',
                      top: '-30px',
                      right: '-30px',
                      width: '60px',
                      height: '60px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '50%',
                      zIndex: 1
                    }} />
                    <div style={{
                      position: 'absolute',
                      bottom: '-20px',
                      left: '-20px',
                      width: '40px',
                      height: '40px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: '50%',
                      zIndex: 1
                    }} />
                    
                    {/* Content */}
                    <div style={{ position: 'relative', zIndex: 2 }}>
                      <div style={{
                        fontSize: '14px',
                        color: 'rgba(255, 255, 255, 0.9)',
                        marginBottom: '6px',
                        fontWeight: '500',
                        letterSpacing: '0.5px'
                      }}>
                        Your Final CEFR Level
                      </div>
                      <div style={{
                        fontSize: '36px',
                        fontWeight: 'bold',
                        color: 'white',
                        marginBottom: '6px',
                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                      }}>
                        {results.global_cefr || (JSON.parse(localStorage.getItem('combined_feedback_data') || '{}')?.overall?.cefr_level ?? '')}
                      </div>
                      <div style={{
                        fontSize: '18px',
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontWeight: '600'
                      }}>
                        {formatScore(results.finalAverage)}/100
                      </div>
                    </div>
                  </div>
                )}
                <h3 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px', color: '#1F2937' }}>
                  Unlock Your Full Results
                </h3>
                <p style={{ color: '#6B7280', marginBottom: '24px', fontSize: '16px' }}>
                  Upgrade to Premium to see detailed feedback and unlock all features
                </p>
                <Button 
                  onClick={handleUpgrade}
                  style={{
                    background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                    color: 'white',
                    padding: '12px 32px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
                  }}
                >
                  Upgrade to Premium
                </Button>
              </div>
            </div>
          )}

          {/* Sidebar */}
          <div className="sidebar">
            {/* Improvement Tips */}
            <div className={`tips-card ${userType === 'guest' ? 'blurred-content' : ''}`} style={{ marginBottom: '20px' }}>
              <h3 className="tips-title" style={{ display: 'flex', alignItems: 'center' }}>
                <MessageCircle className="h-5 w-5 mr-2" />
                Improvement Tips
              </h3>
              <div className="tips-list">
                {improvementTips.slice(0, 3).map((tip, index) => (
                  <div key={index} className="tip-item">
                    <div className="tip-bullet" />
                    <p className="tip-text">{tip}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="action-buttons" style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
              {userType === 'paid' ? (
                <Button 
                  onClick={() => navigate('/dashboard')}
                  style={{ 
                    padding: '8px 16px',
                    fontSize: '14px',
                    width: 'auto',
                    minWidth: 'fit-content'
                  }}
                  size="sm"
                >
                  Return to Dashboard
                </Button>
              ) : (
                <Button 
                  onClick={handleUpgrade}
                  style={{ 
                    padding: '8px 16px',
                    fontSize: '14px',
                    width: 'auto',
                    minWidth: 'fit-content',
                    background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                    color: 'white'
                  }}
                  size="sm"
                >
                  Upgrade to Premium
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamResults;
